/**
 * Web <head> copy for the public marketing surface (mirrors coravu.com).
 *
 * Each public page imports the matching constant, then renders an
 * `<expo-router/head>` block with the title / description / OG / Twitter
 * tags. Keeping the copy here (not inline in pages) means rebrand-day
 * is one file to grep, and there's no drift between `<title>`, `og:title`,
 * and `twitter:title`.
 */

const SITE_BASE_URL = "https://coravu.com";
const OG_IMAGE = `${SITE_BASE_URL}/og/default.png`;
const OG_IMAGE_W = "1200";
const OG_IMAGE_H = "630";

/**
 * Defaults — used by `RootWebHead` for any route without its own override.
 * Mirrors the marketing landing's copy so a stray non-public route still
 * surfaces a sane preview if it ever gets shared (portal routes are
 * noindex'd, so this is purely belt-and-braces).
 */
export const MARKETING_PAGE_TITLE = "Coravu — Precision Talent · Case Recruitment";

export const MARKETING_META_DESCRIPTION =
  "Coravu runs structured case competitions where students prove their thinking — and companies discover talent built on performance, not paperwork.";

export type MarketingMeta = {
  /** Browser tab + og:title + twitter:title. */
  title: string;
  /** meta description + og:description + twitter:description. */
  description: string;
  /** Path part appended to the canonical site origin for og:url. */
  pathname: string;
};

export const META_LANDING: MarketingMeta = {
  title: "Coravu — Where students compete for real jobs",
  description:
    "The talent acquisition platform that replaces résumé-stack hiring with structured competitions. Companies see how candidates actually perform; students compete for real jobs and prize money.",
  pathname: "/",
};

export const META_PRICING: MarketingMeta = {
  title: "Coravu pricing",
  description:
    "Per-competition licensing for SMB recruiters. $4.99 to $3.00 per student. Recruitment Management custom-priced. Free for student organizations.",
  pathname: "/pricing",
};

export const META_FAQ: MarketingMeta = {
  title: "Coravu FAQ",
  description:
    "Common questions about how Coravu works for companies, students, and universities.",
  pathname: "/faq",
};

export const META_CONTACT: MarketingMeta = {
  title: "Contact Coravu",
  description:
    "Get in touch with the Coravu team about running a competition, partnering as a campus, or general inquiries.",
  pathname: "/contact",
};

export const META_PRIVACY: MarketingMeta = {
  title: "Coravu privacy policy",
  description: "How Coravu collects, uses, and protects user data.",
  pathname: "/privacy",
};

export const META_TERMS: MarketingMeta = {
  title: "Coravu terms of service",
  description: "Terms governing use of the Coravu platform.",
  pathname: "/terms",
};

/** Resolved values handy for building `<meta>` content strings. */
export function ogImageUrl(): string {
  return OG_IMAGE;
}

export function canonicalUrl(pathname: string): string {
  return `${SITE_BASE_URL}${pathname}`;
}

export function ogImageDimensions(): { width: string; height: string } {
  return { width: OG_IMAGE_W, height: OG_IMAGE_H };
}
